<template>
	<div
		v-if="hasDomEventsButNotEnough"
		class="p-20 session-media-background rounded-lg overflow-hidden border border-gray-600 shadow"
		:style="{aspectRatio: placeholderAspectRatio}"
	>
		<BaseIcon name="bird" class="w-20 h-20 mr-8 mb-20" />
		<p v-if="domRecordingDisabled" class="text-center">
			Expected to see a visual replay? <br />
			<br />
			Enable "DOM mutations" in the data recording
			<button class="link-blue" @click="openExtensionOptionsPage">extension settings</button> to allow us to
			capture your page.
		</p>
		<p v-else-if="$store.isExtension" class="text-center">
			There's nothing to replay right now. <br />
			<br />
			Replays are working though, so feel free to close this tab <br />
			and try again after there has been some action on your page.
		</p>
		<p v-else class="text-center">
			This session does not contain enough events.<br />
			<br />
			Replays are working though, so you can go back<br />
			and try again after there has been some action on your page.
		</p>
	</div>
	<div v-else-if="hasDomEvents" class="rounded-lg overflow-hidden border border-gray-600 shadow">
		<BaseBanner
			v-if="hasALotOfDomEvents && !isDomRecordingWarningDismissed"
			contentClass="flex w-full gap-8 pr-8"
			class="bg-gray-500 mb-12"
			noColorAccent
		>
			<template #icon><BaseIcon name="mono/info" class="text-red-600 mb-2" /></template>
			<p>
				<span>This Instant Replay contains a lot data, which may cause playback issues. </span>
				<a class="link-blue" target="_blank" href="https://birdeatsbug.com/feature/instant-replay">
					More Info
				</a>
			</p>
			<button class="ml-auto self-start" @click="isDomRecordingWarningDismissed = true">
				<BaseIcon name="mono/delete" class="w-10 h-10 mb-2" />
			</button>
		</BaseBanner>
		<div
			v-if="$store.isExtension && maxRecordedWidth"
			class="flex justify-between bg-gray-650 rounded-t-lg py-10 px-14 flex-wrap"
		>
			<p class="text-gray-300">Drag the "Start" and "End" markers to trim your replay</p>
			<a
				class="text-gray-400 hover:text-gray-300"
				target="_blank"
				href="https://birdeatsbug.com/feature/instant-replay"
			>
				Learn more about replays <BaseIcon name="mono/info" class="mb-2" />
			</a>
		</div>
		<div
			class="relative"
			:class="[$store.isExtension ? 'player-container' : '']"
			@mouseenter="mouseOverDomPlayer = true"
			@mouseleave="mouseOverDomPlayer = false"
		>
			<a
				v-if="mouseOverDomPlayer && !isFullscreen && !$store.isExtension"
				class="absolute top-0 right-0 m-16 text-white bg-gray-500 hover:bg-gray-400 rounded-full px-16 py-4 shadow"
				style="z-index: 2"
				target="_blank"
				href="https://birdeatsbug.com/feature/instant-replay"
				@click.stop
			>
				Learn about replays
				<BaseIcon name="mono/info" size="14" class="ml-8 mb-2" />
			</a>
			<button
				v-if="!isFullscreen"
				tabindex="0"
				class="absolute w-full inset-0 bg-transparent cursor-pointer overlay"
				:style="{'aspect-ratio': playerAspectRatio, zIndex: 1}"
				@click.self="togglePlayPause"
			/>

			<div
				ref="player"
				v-resize="computePlayerScale"
				:style="{
					'aspect-ratio': playerAspectRatio,
					'margin-bottom': '40px',
				}"
				class="w-full"
				@click.self.stop="togglePlayPause"
			>
				<div
					v-if="playerInitialized"
					ref="controller"
					:style="{bottom: isFullscreen ? '0' : '-40px'}"
					class="absolute flex items-center gap-16 w-full h-40 bg-gray-650 border-t border-gray-600 rounded-b-lg px-16 py-12 z-10"
				>
					<!-- CONTROLS LEFT -->
					<div class="flex items-center gap-16">
						<PlayerControlButton
							:label="playerState === 'playing' ? 'Pause' : 'Play'"
							@click="togglePlayPause"
						>
							<BaseIcon v-if="playerState === 'playing'" name="mono/pause" class="w-full h-full" />
							<BaseIcon v-else name="mono/play" class="w-full h-full" />
						</PlayerControlButton>
						<PlayerTimeIndicator
							:timeMs="currentTimeMilliseconds"
							:durationMs="totalTimeMilliseconds"
							:timeClass="domTrimming.isTrimming ? 'text-yellow-500' : ''"
						/>
					</div>
					<!-- PROGRESS BAR -->
					<button
						ref="progress"
						class="progress bg-gray-600 dark:bg-gray-400"
						@click="handleProgressClick"
						@keyup.space.prevent="togglePlayPause"
					>
						<!-- Start trim handle -->
						<DomTrimHandle :isVisible="editTrimming" :position="'start'" />

						<div class="absolute inset-0 overflow-hidden rounded-sm">
							<div :style="{width: `${percentage}%`}" class="progress-step" />
							<!-- End trim percentage -->
							<div
								v-if="progressBarWidthPercentageTrimmedAtEnd"
								class="trimmed-progress absolute"
								:style="{width: progressBarWidthPercentageTrimmedAtEnd, right: 0}"
							/>

							<template v-for="(event, index) in domEventsWithUserInteraction" :key="index">
								<div :style="event.style" class="dom-activity" />
							</template>
							<div :style="{width: `${percentage}%`}" class="progress-handler" />

							<!-- Start trim percentage -->
							<div
								v-if="progressBarWidthPercentageTrimmedAtStart"
								:style="{width: progressBarWidthPercentageTrimmedAtStart}"
								class="trimmed-progress"
							/>
						</div>
						<!-- End trim handle -->
						<DomTrimHandle :isVisible="editTrimming" :position="'end'" />
					</button>
					<!-- CONTROLS RIGHT -->
					<div class="flex items-center gap-14">
						<PlayerControlButton label="Playback speed" @click="setSpeed">
							<span class="font-mono font-bold text-sm block mt-2"> {{ speed }}x </span>
						</PlayerControlButton>
						<PlayerControlButton
							v-if="canUserEditSession && !$store.isExtension && !isFullscreen"
							:label="editTrimming ? 'Save trimming positions' : 'Edit trimming'"
							@click="toggleEditTrimming"
						>
							<BaseIcon v-if="editTrimming" name="mono/check" class="-mt-2 text-green-500" />
							<BaseIcon v-else name="mono/trim" class="w-full h-full" />
						</PlayerControlButton>
						<PlayerControlButton
							:label="isFullscreen ? 'Minimize' : 'Fullscreen'"
							@click="toggleFullScreen"
						>
							<BaseIcon v-if="isFullscreen" name="mono/minimize" class="w-full h-full" />
							<BaseIcon v-else name="mono/fullscreen" class="w-full h-full" />
						</PlayerControlButton>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import throttle from 'lodash/throttle'
import {EventType, IncrementalSource} from 'rrweb'
import {storeToRefs} from 'pinia'

import DomTrimHandle from 'shared/components/DomTrimHandle.vue'
import PlayerControlButton from 'shared/components/PlayerControlButton.vue'
import PlayerTimeIndicator from 'shared/components/PlayerTimeIndicator.vue'
import {useSessionStore} from 'shared/stores/session'

import 'rrweb-player/dist/style.css'
import colors from 'shared/styles/colors'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE

export default {
	name: 'DomPlayer',
	components: {
		DomTrimHandle,
		PlayerControlButton,
		PlayerTimeIndicator,
	},
	setup() {
		const sessionStore = useSessionStore()
		const {session, domTrimming, seekSeconds, currentTimeSeconds, canUserEditSession} = storeToRefs(sessionStore)
		return {
			session,
			domTrimming,
			seekSeconds,
			currentTimeSeconds,
			canUserEditSession,
			sessionStore,
		}
	},
	data: () => ({
		hasDomEvents: false,
		hasDomEventsButNotEnough: false,
		hasALotOfDomEvents: false,
		playerInitialized: false,
		domEventsWithUserInteraction: [],
		isDomRecordingWarningDismissed: false,
		domRecordingDisabled: false,
		maxRecordedWidth: null,
		maxRecordedHeight: null,
		isFullscreen: false,
		currentTimeMilliseconds: 0,
		timer: 0,
		playerState: 'paused',
		totalTimeMilliseconds: null,
		percentage: 0,
		isPlaybackSpeedVisible: false,
		speedOptions: [1, 2, 4],
		speed: 1,
		timerInterval: null,
		mouseOverDomPlayer: false,
		editTrimming: false,
	}),
	// Putting `domEvents` and `player` outside data makes them non-reactive,
	// which is fine, because we don't require Vue reactivity on them.
	// This was done to prevent crashes when trying to play back DOM recordings
	// of large sizes (xxx MB - 1+ GB).
	domEvents: null,
	player: null,
	computed: {
		placeholderAspectRatio() {
			const {windowWidth, windowHeight} = this.session
			return `${windowWidth}/${windowHeight}`
		},
		playerAspectRatio() {
			// Don't show player while the DOM events are still loading or being processed
			if (!this.maxRecordedWidth) return `0/0`
			// Chose aspect ratio based on largest recorded DOM dimension,
			// so that - together with the scaling - no content is cut off,
			// even when the viewport was resized during recording.
			return `${this.maxRecordedWidth}/${this.maxRecordedHeight}`
		},
		progress() {
			return this.$refs.progress
		},
		showTrimHandles() {
			return this.mouseOverDomPlayer && this.$store.isExtension
		},
		endTime() {
			const {trimmedEndTimeInMs} = this.domTrimming
			return trimmedEndTimeInMs && this.domTrimming.onlyPlayTrimmedPart
				? trimmedEndTimeInMs
				: this.totalTimeMilliseconds
		},
		playerTime() {
			let time
			if (this.domTrimming.isTrimming && this.domTrimming.activeTrimHandle === 'end') {
				time = this.domTrimming.trimmedEndTimeInMs
			} else if (this.domTrimming.isTrimming && this.domTrimming.activeTrimHandle === 'start') {
				time = this.domTrimming.trimmedStartTimeInMs
			} else {
				time = this.currentTimeMilliseconds || this.domTrimming.trimmedStartTimeInMs || 0
			}
			return this.formatPlaybackTime(time)
		},
		progressBarWidthPercentageTrimmedAtStart() {
			const {trimmedStartTimeInMs} = this.domTrimming
			if (!trimmedStartTimeInMs) return
			const trimmedPercent = Math.min(1, trimmedStartTimeInMs / this.totalTimeMilliseconds)
			return `${100 * trimmedPercent}%`
		},
		progressBarWidthPercentageTrimmedAtEnd() {
			const {trimmedEndTimeInMs} = this.domTrimming
			if (!trimmedEndTimeInMs) return
			const trimmedPercent = Math.min(1, trimmedEndTimeInMs / this.totalTimeMilliseconds)
			return `${100 - 100 * trimmedPercent}%`
		},
	},
	watch: {
		isFullscreen() {
			this.computePlayerScale()
		},
		currentTimeMilliseconds: {
			immediate: true,
			handler(value) {
				const percent = Math.min(1, value / this.totalTimeMilliseconds)
				this.percentage = 100 * percent
			},
		},
		'domTrimming.trimHandleXPositionInPx': {
			immediate: true,
			handler(clientX) {
				if (!clientX) return
				this.handleProgressClick({clientX})
			},
		},
		'$store.isExtension': {
			immediate: true,
			async handler(isExtension) {
				if (!isExtension || !window.chrome) return
				window.chrome.storage.local.get('recordedEventTypes', ({recordedEventTypes}) => {
					if (recordedEventTypes?.dom === false) this.domRecordingDisabled = true
				})
			},
		},
		seekSeconds(seekSeconds) {
			this.goTo(seekSeconds * 1000)
		},
	},
	async created() {
		this.hasDomEvents = this.session.domEvents || this.session.isInstantReplay
		if (!this.hasDomEvents) {
			return document.dispatchEvent(new CustomEvent('sessionDomEventsLoaded', {detail: {hasDomEvents: false}}))
		}
		const data = await this.sessionStore.dataLoader.getDomEvents(this.session)
		const domEvents = data.domEvents || []
		this.$options.domEvents = domEvents
		const firstDomEventIsoString = domEvents[0] && new Date(domEvents[0].timestamp).toISOString()
		const lastDomEventIsoString = domEvents[0] && new Date(domEvents[domEvents.length - 1].timestamp).toISOString()
		// As soon as Instant Replay is enabled rrweb fires three events (initial event, snapshot event and page info event.
		// If Instant Replay is clicked right after enabling it, these three events don't show any sort of activity,
		// there is just a 0:00 recording. This check is to identify if there has been some minimum possible activity
		// other than the initial three events before creating the DOM player.
		if (domEvents.length <= 5) {
			this.hasDomEventsButNotEnough = true
			return document.dispatchEvent(
				new CustomEvent('sessionDomEventsLoaded', {
					detail: {hasDomEvents: true, firstDomEventIsoString, lastDomEventIsoString},
				})
			)
		}
		if (domEvents.length > 2000) this.hasALotOfDomEvents = true

		this.setDomEventTrimMarkers()
		await this.createPlayer()
		if (!this.$options.player) return
		document.dispatchEvent(
			new CustomEvent('sessionDomEventsLoaded', {
				detail: {hasDomEvents: true, firstDomEventIsoString, lastDomEventIsoString},
			})
		)
		window.dispatchEvent(new CustomEvent('playerInitialized'))
		this.goTo(this.seekSeconds * 1000)
	},
	beforeUnmount() {
		window.removeEventListener('togglePlayPause', this.togglePlayPause)
		this.pause()
		this.stopTimer()
		clearInterval(this.timerInterval)
		this.$options.player = null
		this.domTrimming.resetStore()
	},
	unmounted() {
		// call fullscreen listener cleanup function
		this.fullscreenListener && this.fullscreenListener()
	},
	methods: {
		async createPlayer() {
			await this.$nextTick()
			if (this.$options.player) return
			if (!this.$options.domEvents?.length) return

			const {Replayer} = await import('rrweb')
			if (!this.$refs.player) return setTimeout(this.createPlayer, 50)
			this.$options.player = new Replayer(this.$options.domEvents, {
				root: this.$refs.player,
				skipInactive: false,
				triggerFocus: false,
				mouseTail: {
					strokeStyle: 'rgba(0,0,0,0)',
				},
			})
			await this.$nextTick()

			// listener for when fullscreen is changed from anything other than
			// the toggle fullscreen button
			this.fullscreenListener = this.onFullscreenChange(() => {
				this.isFullscreen = this.isInFullscreenMode()
			})

			this.totalTimeMilliseconds = this.$options.player.getMetaData().totalTime
			this.domTrimming.replayTotalTimeInMs = this.totalTimeMilliseconds

			this.$options.player.on('state-change', states => {
				const {player} = states
				if (player?.value && this.playerState !== player.value) {
					this.playerState = player.value
					switch (this.playerState) {
						case 'playing':
							this.loopTimer()
							break
						case 'paused':
							this.stopTimer()
							break
						default:
							break
					}
				}
			})
			this.$options.player.on('finish', () => {
				this.currentTimeMilliseconds = this.totalTimeMilliseconds
				window.dispatchEvent(new CustomEvent('playerFinished'))
			})
			// also toggle play pause when dom player is clicked
			window.addEventListener('togglePlayPause', this.togglePlayPause)

			this.computeUserInteractionEventsAndEventsForPlayerSizing()

			this.computePlayerScale()

			this.playerInitialized = true
		},
		computeUserInteractionEventsAndEventsForPlayerSizing() {
			const {context} = this.$options.player.service.state
			const totalEvents = context.events.length
			const start = context.events[0].timestamp
			const end = context.events[totalEvents - 1].timestamp
			const domEventsWithUserInteraction = []
			// calculate tag position.
			const position = (startTime, endTime, tagTime) => {
				const sessionDuration = endTime - startTime
				const eventDuration = endTime - tagTime
				const eventPosition = 100 - (eventDuration / sessionDuration) * 100
				return eventPosition.toFixed(2)
			}
			/**
			 * Calculate events position and mark is as a green to indicate actvity on player timeline.
			 * Events that have within 1 (~ 1second) position difference to it's previous adjacent event
			 * is marked as same event with only it's width increasing. More than 1 (position difference)
			 * is considered a gap in timeline and a new div is rendered from there on. This is to decrease
			 * the number rendered highlighted div's on timeline in case of large number of events
			 */
			let firstEventAfterGap, previousEventPosition
			let width = 0.2

			let maxRecordedWidth = 0
			let maxRecordedHeight = 0

			context.events.forEach(event => {
				const eventWidth = event?.data?.width
				const eventHeight = event?.data?.height
				if (eventWidth > maxRecordedWidth) maxRecordedWidth = eventWidth
				if (eventHeight > maxRecordedHeight) maxRecordedHeight = eventHeight

				if (this.isUserInteraction(event)) {
					if (!firstEventAfterGap) firstEventAfterGap = event
					const currentEventPosition = position(start, end, event.timestamp)
					const positionDifference = currentEventPosition - previousEventPosition
					previousEventPosition = currentEventPosition

					if (positionDifference < 1 && position > 0) {
						width += 0.2
						return
					}
					domEventsWithUserInteraction.push({
						style: {
							background: colors['gray-600'],
							left: `${position(start, end, firstEventAfterGap.timestamp)}%`,
							width: `${width}px`,
						},
					})
					firstEventAfterGap = event
					width = 0.2
				}
			})
			this.domEventsWithUserInteraction = domEventsWithUserInteraction
			this.maxRecordedWidth = maxRecordedWidth
			this.maxRecordedHeight = maxRecordedHeight
		},
		async computePlayerScale() {
			await this.$nextTick()
			if (!this.$refs.player) return
			const {width: playerWidth, height: playerHeight} = this.$refs.player.getBoundingClientRect()
			// Scale based on the biggest recorded DOM, so that the scale is so small that all content is shown
			// in the player, even when viewport size and aspect ratio changed during recording.
			const scaleX = playerWidth / this.maxRecordedWidth
			const scaleY = playerHeight / this.maxRecordedHeight
			const scale = Math.min(scaleX, scaleY)
			const playbackSurface = document.getElementsByClassName('replayer-wrapper')?.[0]
			if (playbackSurface) playbackSurface.style.transform = `scale(${scale})`

			if (!this.$refs.progress) return
			const {width, left, right} = this.$refs.progress.getBoundingClientRect()
			this.domTrimming.domProgressBarPositionInPx.width = width
			this.domTrimming.domProgressBarPositionInPx.left = left
			this.domTrimming.domProgressBarPositionInPx.right = right
		},
		isInFullscreenMode() {
			return document.webkitIsFullScreen || document.mozFullScreen || document.msFullscreenElement
		},
		toggleFullScreen() {
			if (this.$refs.player) {
				this.isFullscreen ? this.exitFullscreen() : this.openFullscreen(this.$refs.player)
			}
		},
		openFullscreen(el) {
			if (el.requestFullscreen) {
				return el.requestFullscreen()
			} else if (el.mozRequestFullScreen) {
				/* Firefox */
				return el.mozRequestFullScreen()
			} else if (el.webkitRequestFullscreen) {
				/* Chrome, Safari and Opera */
				return el.webkitRequestFullscreen()
			} else if (el.msRequestFullscreen) {
				/* IE/Edge */
				return el.msRequestFullscreen()
			}
		},
		exitFullscreen() {
			if (document.exitFullscreen) {
				return document.exitFullscreen()
			} else if (document.mozExitFullscreen) {
				/* Firefox */
				return document.mozExitFullscreen()
			} else if (document.webkitExitFullscreen) {
				/* Chrome, Safari and Opera */
				return document.webkitExitFullscreen()
			} else if (document.msExitFullscreen) {
				/* IE/Edge */
				return document.msExitFullscreen()
			}
		},
		onFullscreenChange(handler) {
			document.addEventListener('fullscreenchange', handler)
			document.addEventListener('webkitfullscreenchange', handler)
			document.addEventListener('mozfullscreenchange', handler)
			document.addEventListener('MSFullscreenChange', handler)

			return () => {
				document.removeEventListener('fullscreenchange', handler)
				document.removeEventListener('webkitfullscreenchange', handler)
				document.removeEventListener('mozfullscreenchange', handler)
				document.removeEventListener('MSFullscreenChange', handler)
			}
		},
		formatPlaybackTime(ms) {
			if (ms <= 0) {
				return '00:00'
			}
			const hour = Math.floor(ms / HOUR)
			ms = ms % HOUR
			const minute = Math.floor(ms / MINUTE)
			ms = ms % MINUTE
			const second = Math.floor(ms / SECOND)
			if (hour) {
				return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(
					2,
					'0'
				)}`
			}
			return `${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
		},
		loopTimer() {
			this.stopTimer()
			const update = () => {
				this.currentTimeMilliseconds = this.$options.player.getCurrentTime()

				if (this.currentTimeMilliseconds < this.endTime) {
					this.timer = requestAnimationFrame(update)
					return
				} else {
					this.pause()
				}
			}
			this.timer = requestAnimationFrame(update)
		},
		stopTimer() {
			if (this.timer) {
				cancelAnimationFrame(this.timer)
				this.timer = 0
			}
		},
		togglePlayPause() {
			let fromTime
			const {trimmedStartTimeInMs} = this.domTrimming

			switch (this.playerState) {
				case 'playing':
					this.pause()
					window.dispatchEvent(new CustomEvent('playbackPause'))
					break
				case 'paused':
					if (!fromTime) fromTime = this.currentTimeMilliseconds || trimmedStartTimeInMs
					if (fromTime >= this.endTime) {
						fromTime =
							trimmedStartTimeInMs && this.domTrimming.onlyPlayTrimmedPart ? trimmedStartTimeInMs : null
					}
					this.play(fromTime)
					break
				default:
					break
			}
		},
		play(fromTime) {
			if (this.playerState !== 'paused') {
				return
			}
			// we fire the seek event 4 times every second (similar to a video)
			this.timerInterval = setInterval(() => {
				this.currentTimeSeconds = this.currentTimeMilliseconds / 1000
			}, 250)

			this.$options.player.play(fromTime)
		},
		pause() {
			if (this.playerState !== 'playing') {
				return
			}
			this.$options.player.pause()
			clearInterval(this.timerInterval)
		},
		goTo(timeOffsetInMs) {
			if (!timeOffsetInMs) return
			this.currentTimeMilliseconds = timeOffsetInMs
			const isPlaying = this.playerState === 'playing'
			this.pause()
			this.play(timeOffsetInMs)
			if (!isPlaying) {
				this.pause()
			}
			this.sessionStore.seek(timeOffsetInMs / 1000)
		},
		goToTimeWhileTrimming: throttle(function (timeOffset) {
			this.goTo(timeOffset)
		}, 1000),
		handleProgressClick(event) {
			const x = event.clientX - this.domTrimming.domProgressBarPositionInPx.left
			let percent = x / this.domTrimming.domProgressBarPositionInPx.width
			if (percent < 0) {
				percent = 0
			} else if (percent > 1) {
				percent = 1
			}
			const timeOffset = this.totalTimeMilliseconds * percent

			// Once user clicks outside of the trimmed time on progressbar, we allow playback
			// to be played from anywhere
			const {activeTrimHandle, trimEndPosition, trimStartPosition} = this.domTrimming

			if (!activeTrimHandle && (event.clientX > trimEndPosition || event.clientX < trimStartPosition))
				this.domTrimming.onlyPlayTrimmedPart = false

			if (activeTrimHandle === 'start') {
				this.domTrimming.trimmedStartTimeInMs = timeOffset
				return this.goToTimeWhileTrimming(timeOffset)
			}
			// from trim direction end don't goto the timeOffset in progress bar, and return early
			else if (activeTrimHandle === 'end') return (this.domTrimming.trimmedEndTimeInMs = timeOffset)

			this.goTo(timeOffset)
		},
		setSpeed() {
			const newSpeed = this.speedOptions[this.speedOptions.indexOf(this.speed) + 1] || 1
			let isPlaying = this.playerState === 'playing'
			this.speed = newSpeed
			if (isPlaying) {
				this.pause()
			}
			this.$options.player.setConfig({speed: newSpeed})
			if (isPlaying) {
				this.play(this.currentTimeMilliseconds)
			}
		},
		isUserInteraction(event) {
			if (event.type !== EventType.IncrementalSnapshot) {
				return false
			}
			return event.data.source > IncrementalSource.Mutation && event.data.source <= IncrementalSource.Input
		},
		setDomEventTrimMarkers() {
			this.editTrimming = this.$store.isExtension
			if (!this.session?.domEventTrimMarkers) return
			this.domTrimming.trimmedStartTimeInMs = this.session.domEventTrimMarkers.startTimeInMs
			this.domTrimming.trimmedEndTimeInMs = this.session.domEventTrimMarkers.endTimeInMs
		},
		async openExtensionOptionsPage() {
			if (!this.$store.isExtension || !window.chrome) return
			window.chrome.runtime.openOptionsPage()
		},
		async toggleEditTrimming() {
			if (!this.canUserEditSession) return
			this.editTrimming = !this.editTrimming
			// When exiting trimming edit mode, save updated markers
			if (!this.editTrimming) {
				const updateSessionDomEventTrimMarkers = (
					await import('shared/apollo/updateSessionDomEventTrimMarkers')
				).default
				updateSessionDomEventTrimMarkers({
					sessionId: this.session.id,
					domEventTrimMarkers: {
						startTimeInMs: this.domTrimming.trimmedStartTimeInMs,
						endTimeInMs: this.domTrimming.trimmedEndTimeInMs,
					},
					userId: this.$auth.user.value?.sub,
				})
			}
		},
	},
}
</script>

<style>
/* define replayer-wrapper styles as important to prevent overrides by SDK */
.replayer-wrapper > iframe {
	border: none !important;
}

.replayer-wrapper {
	position: absolute !important;
	top: unset !important;
	left: unset !important;
}

.replayer-mouse.active::after {
	animation: click 0.5s ease-in-out 1;
}

.replayer-mouse::after {
	content: '';
	display: inline-block;
	width: 40px;
	height: 40px;
	background: rgb(73, 80, 246);
	border-radius: 100%;
	transform: translate(-25%, -25%);
	opacity: 0;
}

@keyframes click {
	0% {
		opacity: 0;
		width: 0px;
		height: 0px;
	}
	50% {
		opacity: 0.8;
		width: 40px;
		height: 40px;
	}
}

.player-frame svg iframe {
	position: relative;
	z-index: -1;
}

.replayer-mouse {
	background-image: url('../icons/mono/mouse-cursor.svg');
}

.h-90p {
	height: 90%;
}
</style>

<style scoped>
.player-container {
	border-top-left-radius: 0px;
	border-top-right-radius: 0px;
	box-shadow: 0 0 24px rgba (0, 0, 0, 0.4);
}

.progress {
	flex: 1;
	height: 16px;
	overflow: visible;
	position: relative;
	border-radius: 4px;
	cursor: pointer;
	box-sizing: border-box;
	box-shadow: none;
}

.progress-step {
	height: 100%;
	position: absolute;
	left: 0;
	top: 0;
}

.dom-activity {
	height: 6px;
	position: absolute;
	margin-top: 5px;
	top: 0;
}

.progress-handler {
	position: absolute;
	top: 0;
	height: 16px;
	background-color: var(--gray-100);
	opacity: 0.6;
}

.trimmed-progress {
	position: absolute;
	top: 0;
	z-index: 2;
	height: 16px;
	background: var(--gray-800);
	opacity: 0.7;
}
</style>
